/**
 * Isolates independently reviewable pending asset rows so editing one row's
 * controlled decision does not make every visible card render again.
 */

import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import type {
  AssetDto,
  CampaignDesiredStateDto,
} from 'src/store/generated/leela-api-generated';
import type { RuleMap } from 'src/types/assetEval';
import { assetStrategyKey, isSameAssetStrategy } from 'src/utils/assetKeys';
import type { ScheduleWarning } from 'src/utils/computeScheduleWarnings';
import { resolveStrategyLabel } from 'src/utils/strategyLabels';
import { AssetCard } from './AssetCard';
import type { AssetViewPreferences } from './assetViewFields';
import type { RejectReasonSelection } from './RejectReasonSelect';

export interface AssetPendingViewProps {
  programAssets: (object[] & AssetDto[]) | undefined;
  campaigns: (object[] & CampaignDesiredStateDto[]) | undefined;
  programId: string;
  preferences: AssetViewPreferences;
  selectedAssets: AssetDto[];
  action: (asset: AssetDto) => void;
  releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  isReleaseDatesFetching?: boolean;
  schedules?: Map<string, { startTime: string; endTime: string }>;
  scheduleWarnings?: Map<string, ScheduleWarning[]>;
  onScheduleChange?: (key: string, schedule: { startTime: string; endTime: string }) => void;
  sportsCampaignIds?: Set<string>;
  // productStrategyId → display name, shown inline on each card.
  strategyLabels?: Map<string, string>;
  // Rule metadata for the per-asset evaluation badge; passed straight through to each card.
  ruleMap?: RuleMap;
  rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>;
  onRejectReasonChange?: (asset: AssetDto, value: RejectReasonSelection) => void;
}

interface PendingAssetCardProps {
  readonly action: (asset: AssetDto) => void;
  readonly asset: AssetDto;
  readonly campaigns: CampaignDesiredStateDto[];
  readonly isReleaseDatesFetching?: boolean;
  readonly isSelected: boolean;
  readonly isSports: boolean;
  readonly preferences: AssetViewPreferences;
  readonly programId: string;
  readonly rejectReasonValue?: RejectReasonSelection;
  readonly releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  readonly ruleMap?: RuleMap;
  readonly schedule?: { startTime: string; endTime: string };
  readonly strategyLabel: string;
  readonly warnings?: ScheduleWarning[];
  readonly onRejectReasonChange?: (asset: AssetDto, value: RejectReasonSelection) => void;
  readonly onScheduleChange?: (key: string, schedule: { startTime: string; endTime: string }) => void;
}

const PendingAssetCard = React.memo(({
  action,
  asset,
  campaigns,
  isReleaseDatesFetching,
  isSelected,
  isSports,
  preferences,
  programId,
  rejectReasonValue,
  releaseDatesByGti,
  ruleMap,
  schedule,
  strategyLabel,
  warnings,
  onRejectReasonChange,
  onScheduleChange,
}: PendingAssetCardProps) => {
  const assetKey = assetStrategyKey(asset);
  const handleRejectReasonChange = React.useCallback((value: RejectReasonSelection) => {
    onRejectReasonChange?.(asset, value);
  }, [asset, onRejectReasonChange]);
  const handleScheduleChange = React.useCallback((value: {
    startTime: string;
    endTime: string;
  }) => {
    onScheduleChange?.(assetKey, value);
  }, [assetKey, onScheduleChange]);

  return (
    <Column heights={['fill']} spacingInset="400">
      <AssetCard
        asset={asset}
        campaigns={campaigns}
        isReleaseDatesFetching={isReleaseDatesFetching}
        isSelected={isSelected}
        preferences={preferences}
        programId={programId}
        rejectReasonValue={rejectReasonValue}
        releaseDatesByGti={releaseDatesByGti}
        ruleMap={ruleMap}
        schedule={isSports ? schedule : undefined}
        strategyLabel={strategyLabel}
        warnings={isSports ? warnings : undefined}
        onRejectReasonChange={onRejectReasonChange ? handleRejectReasonChange : undefined}
        onScheduleChange={isSports && onScheduleChange ? handleScheduleChange : undefined}
        onSelect={action}
      />
    </Column>
  );
});
PendingAssetCard.displayName = 'PendingAssetCard';

const EMPTY_CAMPAIGNS: CampaignDesiredStateDto[] = [];

export const AssetPendingView = ({
  programAssets,
  campaigns,
  programId,
  preferences,
  selectedAssets,
  action,
  releaseDatesByGti,
  isReleaseDatesFetching,
  schedules,
  scheduleWarnings,
  onScheduleChange,
  sportsCampaignIds,
  strategyLabels,
  ruleMap,
  rejectReasonValues,
  onRejectReasonChange,
}: AssetPendingViewProps) => (
  <Row alignmentVertical="stretch" spacingInset="400" widths="fit" wrap="down">
    {(programAssets ?? []).map((asset: AssetDto) => {
      const key = assetStrategyKey(asset);
      const isSports = Boolean(
        asset.campaignId && sportsCampaignIds?.has(asset.campaignId),
      );
      return (
        <PendingAssetCard
          action={action}
          asset={asset}
          campaigns={campaigns ?? EMPTY_CAMPAIGNS}
          isReleaseDatesFetching={isReleaseDatesFetching}
          isSelected={selectedAssets.some((selectedAsset) =>
            isSameAssetStrategy(selectedAsset, asset),
          )}
          isSports={isSports}
          key={key}
          preferences={preferences}
          programId={programId}
          rejectReasonValue={rejectReasonValues?.get(key)}
          releaseDatesByGti={releaseDatesByGti}
          ruleMap={ruleMap}
          schedule={schedules?.get(key)}
          strategyLabel={resolveStrategyLabel(
            strategyLabels,
            asset.productStrategyId,
          )}
          warnings={scheduleWarnings?.get(key)}
          onRejectReasonChange={onRejectReasonChange}
          onScheduleChange={onScheduleChange}
        />
      );
    })}
  </Row>
);
